import React from 'react';
import { Circle, Check } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';

import type { SurfaceVariant } from '@/components/ui/types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Props = Omit<React.ComponentPropsWithRef<typeof Badge>, 'variant'> & {
  variant?: React.ComponentPropsWithRef<typeof Badge>['variant'] | 'accepted';
  surface?: SurfaceVariant;
  suffix?: React.ReactNode;
};

/** Appended after `badgeVariants` on `Badge` — do not set `text-*` here or it overrides semantic `text-success` etc. */
const statusBadgeSurface = cva('', {
  variants: {
    surface: {
      solid: '',
      /** Appended after `badgeVariants` in `Badge`'s `cn()` so these win for bg/hover. */
      transparent: 'bg-transparent text-subtle px-0 border-none',
    },
  },
  defaultVariants: {
    surface: 'solid',
  },
});

/** Icon uses explicit tone so it tracks `variant` (including `accepted` → success) on any `surface`. */
const statusBadgeIconTone = cva('', {
  variants: {
    tone: {
      default: 'text-subtle',
      destructive: 'text-red-600',
      success: 'text-green-600',
      warning: 'text-yellow-600',
      disabled: 'text-subtlest',
    },
  },
  defaultVariants: {
    tone: 'default',
  },
});

export const StatusBadge = React.forwardRef<HTMLSpanElement, Props>(
  ({ className, suffix, variant, surface = 'solid', children, ...props }, ref) => {
    const Icon = variant === 'accepted' ? Check : Circle;
    const badgeVariant = variant === 'accepted' ? 'success' : variant;
    const iconTone = badgeVariant ?? 'default';
    const weight = variant === 'accepted' ? 'bold' : 'fill';
    const size = variant === 'accepted' ? `!size-3` : `!size-2`;

    return (
      <span ref={ref} className={cn('flex items-center gap-1 rounded-md p-1', suffix && 'bg-dark-300', className)}>
        <Badge {...props} variant={badgeVariant} className={statusBadgeSurface({ surface })}>
          <span className="flex items-center gap-1">
            <Icon weight={weight} size={8} className={cn(size, statusBadgeIconTone({ tone: iconTone }))} />
            {children}
          </span>
        </Badge>
        {suffix && <span className="text-xs text-default">{suffix}</span>}
      </span>
    );
  },
);

StatusBadge.displayName = 'StatusBadge';
